/**
 * Shared limits and defaults. These are the single source of truth for the API,
 * the admin panel, and (mirrored by hand) the Kotlin TV app.
 */

// ---------------------------------------------------------------------------
// Media upload limits
//
// v1 accepts H.264 mp4 only, at most 1080p. HEVC is rejected because with no
// server-side transcoding it breaks both the admin-panel <video> preview and the
// client-side thumbnail grab in browsers without hardware HEVC decode, and 4K
// HEVC is unsafe on Fire TV Stick decoders. MediaConvert lifts this in v2.
// ---------------------------------------------------------------------------

export const ACCEPTED_VIDEO_MIME = ['video/mp4'] as const;
export const ACCEPTED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ACCEPTED_MIME = [...ACCEPTED_VIDEO_MIME, ...ACCEPTED_IMAGE_MIME] as const;

/** Video codecs accepted in v1. Anything else is rejected with guidance copy. */
export const ACCEPTED_VIDEO_CODECS = ['avc1', 'h264', 'avc'] as const;

export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const MAX_VIDEO_WIDTH = 1920;
export const MAX_VIDEO_HEIGHT = 1080;

/** S3 multipart part size. Minimum allowed by S3 is 5 MiB (except the final part). */
export const UPLOAD_PART_SIZE = 25 * 1024 * 1024;
/** Presigned part URLs are minted in small batches as the upload progresses. */
export const UPLOAD_PART_URL_TTL_SEC = 15 * 60;
export const UPLOAD_PART_URL_BATCH = 4;
/** Rows for uploads that never complete are swept after this long. */
export const UPLOAD_ABANDON_TTL_SEC = 24 * 60 * 60;

export const THUMBNAIL_MAX_EDGE = 480;

// ---------------------------------------------------------------------------
// Playback defaults
// ---------------------------------------------------------------------------

export const DEFAULT_IMAGE_DURATION_SEC = 8;
export const DEFAULT_VIDEO_REPEAT_COUNT = 1;
export const DEFAULT_FIT = 'contain' as const;
export const DEFAULT_BACKGROUND_COLOR = '#000000';

/**
 * How often the player re-runs the resolver. The resolver is a cheap pure
 * function over a small object, so a tight cadence costs nothing and keeps
 * daypart boundaries visually punctual.
 */
export const RESOLVE_INTERVAL_SEC = 15;

// ---------------------------------------------------------------------------
// Device polling
// ---------------------------------------------------------------------------

export const HEARTBEAT_SEC = 45;
/** Random +/- spread so a fleet rebooting together does not thunder. */
export const HEARTBEAT_JITTER_SEC = 10;
export const PLAYBACK_FLUSH_SEC = 15 * 60;
export const PAIRING_POLL_SEC = 3;

/** Consecutive 401s before the device wipes its token and re-enters pairing. */
export const DEVICE_UNAUTHORIZED_STRIKES = 3;

/** Download retry backoff, in seconds. */
export const DOWNLOAD_RETRY_BASE_SEC = 30;
export const DOWNLOAD_RETRY_MAX_SEC = 10 * 60;

/** Clock skew (device vs server) above which the fleet dashboard raises a flag. */
export const CLOCK_SKEW_WARN_MS = 60_000;

// ---------------------------------------------------------------------------
// Device storage budget
//
// A Fire TV Stick 4K has 8 GB flash with roughly 5 GB usable; the 4K Max 2nd gen
// has 16 GB. Until a device reports its real free space we assume the small one.
// ---------------------------------------------------------------------------

export const DEFAULT_DEVICE_BUDGET_BYTES = 2.5 * 1024 * 1024 * 1024;
/** Kept free for the staged OTA APK and in-flight temp files. */
export const DEVICE_STORAGE_HEADROOM_BYTES = 400 * 1024 * 1024;
/** Fraction of reported free space the media cache may occupy. */
export const DEVICE_CACHE_FRACTION = 0.6;

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const OTP_LENGTH = 6;
export const OTP_TTL_SEC = 10 * 60;
export const OTP_MAX_ATTEMPTS = 5;
/** Minimum gap between OTP requests for one address. */
export const OTP_RESEND_COOLDOWN_SEC = 60;
export const OTP_DAILY_CAP = 10;

export const SESSION_TTL_SEC = 7 * 24 * 60 * 60;

/** Displayed on the TV. Unambiguous alphabet: no O/0, I/1, S/5, B/8. */
export const PAIRING_CODE_ALPHABET = 'ACDEFGHJKLMNPQRTUVWXY2346789';
export const PAIRING_CODE_LENGTH = 6;
export const PAIRING_TTL_SEC = 15 * 60;
/** Failed lookups against one code before it is burned. */
export const PAIRING_MAX_MISSES = 20;

// ---------------------------------------------------------------------------
// Monitoring
// ---------------------------------------------------------------------------

/** No heartbeat for this long => offline. Generous vs HEARTBEAT_SEC to avoid flapping. */
export const DEVICE_OFFLINE_THRESHOLD_SEC = 12 * 60;
export const COMMAND_TTL_SEC = 24 * 60 * 60;

// ---------------------------------------------------------------------------
// Rate limits: [max requests, window seconds]
// ---------------------------------------------------------------------------

export const RATE_LIMITS = {
  // Every screen in a store shares one public address, so the per-IP budgets are
  // sized for a person commissioning several televisions at once with the odd
  // mistype, not for one device on a clean run. The earlier numbers allowed five
  // app starts an hour per STORE, and the panel's own recovery advice for a
  // lapsed code is to relaunch the app — which spends another one. An installer
  // up a ladder would have been rate limited by following the instructions.
  //
  // Guessing is bounded by the per-code budget and by PAIRING_MAX_MISSES burning
  // the code outright, not by these; the per-IP limits only need to bound flood.
  pairInitPerIp: [20, 60 * 60],
  // Generous enough that PAIRING_POLL_SEC is achievable for the whole life of a
  // code, with room for a retry after a dropped connection: a device polling
  // every 3 seconds for the full 15 minutes uses 300.
  pairPollPerCode: [360, 15 * 60],
  // Eight televisions can be pairing at once from one shop, which is more than
  // any of these stores has. At 3-second polling this is under three requests a
  // second from one address for a read, so it bounds flood without bounding work.
  pairPollPerIp: [2400, 15 * 60],
  otpRequestPerEmail: [5, 60 * 60],
  otpRequestPerIp: [10, 60 * 60],
} as const satisfies Record<string, readonly [number, number]>;
